import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [searchFocus, setSearchFocus] = useState(false);
  const navigate = useNavigate();
  const catRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);

  const results = useMemo(() => {
    const kw = query.trim().toLowerCase();
    if (!kw) return [];
    return articles
      .filter(
        (item) =>
          item.title.toLowerCase().includes(kw) ||
          item.summary.toLowerCase().includes(kw) ||
          item.tags.some((tag) => tag.toLowerCase().includes(kw)),
      )
      .slice(0, 5);
  }, [query]);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (catRef.current && !catRef.current.contains(target)) setCatOpen(false);
      if (searchRef.current && !searchRef.current.contains(target)) setSearchFocus(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const gotoArticle = (slug: string) => {
    setSearchFocus(false);
    setQuery('');
    setMenuOpen(false);
    navigate(`/article/${slug}`);
  };

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
      isActive ? 'bg-primary-100 text-primary-700' : 'text-foreground-600 hover:text-primary-600'
    }`;

  return (
    <header className="sticky top-0 z-50 border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="flex w-full items-center gap-4 px-4 py-3 md:px-8">
        <Link to="/" className="flex items-center gap-3" aria-label="美日吃瓜首页">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-eye-line text-xl"></i>
          </span>
          <span className="flex flex-col leading-tight">
            <span className="font-heading text-lg font-bold text-foreground-950">美日吃瓜</span>
            <span className="hidden text-xs text-foreground-500 sm:block">围观美日文化冲突名场面</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="主导航">
          <NavLink to="/" className={linkClass} end>
            首页
          </NavLink>
          <NavLink to="/topics" className={linkClass}>
            话题总览
          </NavLink>
          <div className="relative" ref={catRef}>
            <button
              type="button"
              onClick={() => setCatOpen((prev) => !prev)}
              aria-expanded={catOpen}
              className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium text-foreground-600 transition-colors hover:text-primary-600"
            >
              栏目
              <i className={`ri-arrow-down-s-line transition-transform ${catOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {catOpen && (
              <div className="absolute left-0 top-full mt-2 w-64 rounded-lg border border-background-200 bg-background-50 p-2">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    onClick={() => setCatOpen(false)}
                    className="flex items-center gap-3 rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-600"
                  >
                    <i className={`${cat.icon} text-base text-primary-500`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>
            )}
          </div>
          <NavLink to="/about" className={linkClass}>
            关于我们
          </NavLink>
        </nav>

        <div className="ml-auto hidden md:block" ref={searchRef}>
          <div className="relative">
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onFocus={() => setSearchFocus(true)}
              placeholder="搜索话题、文章或标签"
              className="w-56 rounded-md border border-background-300 bg-background-100 px-3 py-2 pl-9 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50 lg:w-64"
            />
            <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400"></i>
            {searchFocus && query.trim() && (
              <div className="absolute right-0 top-full mt-2 w-80 rounded-lg border border-background-200 bg-background-50 p-2">
                {results.length === 0 ? (
                  <p className="px-3 py-3 text-sm text-foreground-500">没有找到相关内容，换个关键词试试。</p>
                ) : (
                  results.map((item) => (
                    <button
                      key={item.slug}
                      type="button"
                      onClick={() => gotoArticle(item.slug)}
                      className="flex w-full cursor-pointer flex-col items-start gap-1 rounded-md px-3 py-2 text-left transition-colors hover:bg-background-100"
                    >
                      <span className="line-clamp-1 text-sm font-medium text-foreground-900">{item.title}</span>
                      <span className="text-xs text-foreground-500">{item.categoryName}</span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        <button
          type="button"
          aria-label="打开菜单"
          onClick={() => setMenuOpen((prev) => !prev)}
          className="ml-auto flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-700 lg:hidden"
        >
          <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
        </button>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 py-4 lg:hidden">
          <div className="relative mb-4 md:hidden">
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="搜索话题、文章或标签"
              className="w-full rounded-md border border-background-300 bg-background-100 px-3 py-2 pl-9 text-sm text-foreground-800 outline-none focus:border-primary-400"
            />
            <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400"></i>
          </div>
          {query.trim() && (
            <div className="mb-4 rounded-lg border border-background-200 p-2">
              {results.length === 0 ? (
                <p className="px-3 py-2 text-sm text-foreground-500">没有找到相关内容。</p>
              ) : (
                results.map((item) => (
                  <button
                    key={item.slug}
                    type="button"
                    onClick={() => gotoArticle(item.slug)}
                    className="block w-full cursor-pointer rounded-md px-3 py-2 text-left text-sm text-foreground-800 hover:bg-background-100"
                  >
                    {item.title}
                  </button>
                ))
              )}
            </div>
          )}
          <nav className="flex flex-col gap-1" aria-label="移动端导航">
            <Link
              to="/"
              onClick={() => setMenuOpen(false)}
              className="rounded-md px-3 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100"
            >
              首页
            </Link>
            <Link
              to="/topics"
              onClick={() => setMenuOpen(false)}
              className="rounded-md px-3 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100"
            >
              话题总览
            </Link>
            <p className="mt-2 px-3 text-xs uppercase tracking-widest text-foreground-400">栏目</p>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-3 rounded-md px-3 py-2 text-sm text-foreground-700 hover:bg-background-100"
              >
                <i className={`${cat.icon} text-base text-primary-500`}></i>
                {cat.name}
              </Link>
            ))}
            <Link
              to="/about"
              onClick={() => setMenuOpen(false)}
              className="mt-2 rounded-md px-3 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100"
            >
              关于我们
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}